import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import { clearServiceDetails } from "./serviceSlice";
import {
  createService,
  getServiceByID,
  updateService,
} from "./services/service.services";

import FormTextArea from "../../components/formFields/FormTextArea";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ServiceID = location.state?.ServiceID;
  const ServiceDetail = useAppSelector((state) => state.service.ServiceDetail);
  const loading = useAppSelector((state) => state.service.loading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (ServiceID) {
      dispatch(getServiceByID(ServiceID));
    }
    return () => {
      dispatch(clearServiceDetails());
    };
  }, [ServiceID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ServiceDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (ServiceID) {
        const editPayload = {
          ID: ServiceID,

          ServiceName: values.ServiceName ? values.ServiceName : "",
          Description: values.Description ? values.Description : "",
          Charges: values.Charges ? +values.Charges : null,
          OrderNo: values.OrderNo ? +values.OrderNo : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          ServiceName: values.ServiceName ? values.ServiceName : "",
          Description: values.Description ? values.Description : "",
          Charges: values.Charges ? +values.Charges : null,
          OrderNo: values.OrderNo ? +values.OrderNo : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createService(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/service");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ServiceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {ServiceID ? "Update Service" : "Add Service"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="ServiceName"
                    label="Service Name"
                    placeholder="Service Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="Charges"
                    label="Charges"
                    placeholder="0"
                    min={0}
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem rowSpan={2}>
                  <Field
                    name="Description"
                    label="Description"
                    rows={5}
                    placeholder="Description"
                    component={FormTextArea}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`OrderNo`}
                    label="Order No"
                    type={"number"}
                    placeholder="0"
                    min={0}
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={ServiceID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/service")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateService;
